import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ChevronDown, Clock, Bus, GraduationCap as ProfessorIcon, Settings } from 'lucide-react';
import './App.css';
import Layout from './Components/Layout';
import Grade from './pages/grade/Grade';
import GerenciarMatriculas from './pages/matriculas/GerenciarMatriculas';
import CadastrarAluno from './pages/matriculas/CadastrarAluno';
import Bolsistas from './pages/bolsistas/Bolsistas';
import AssistenciaSocial from './pages/assistencia-social/AssistenciaSocial';
import Estoque from './pages/estoque/Estoque';
import Meritocracia from './pages/acompanhamento/Meritocracia';
import CaraterAdmin from './pages/acompanhamento/CaraterAdmin';
import Notas from './pages/acompanhamento/Notas';
import Planejamento from './pages/acompanhamento/Planejamento';
import Pontos from './pages/pontos/Pontos';
import SelecionarInstituicao from './pages/acesso/SelecionarInstituicao';
import AjusteGrade from './pages/grade/AjusteGrade';
import GradeTurmas from './pages/grade/GradeTurmas';
import Turmas from './pages/grade/Turmas';
import Professores from './pages/grade/Professores';
import Permissoes from './pages/seguranca/Permissoes';
import AreasConfig from './pages/seguranca/AreasConfig';
import Foguinhos from './pages/acompanhamento/Foguinhos';
import Ocorrencias from './pages/acompanhamento/Ocorrencias';
import FeedbackDay from './pages/acompanhamento/FeedbackDay';
import Listas from './pages/listas/Listas';
import ListaDetalhe from './pages/listas/ListaDetalhe';
import Termos from './pages/termos/Termos';
import TermoDetalhe from './pages/termos/TermoDetalhe';
import Devolucoes from './pages/devolucoes/Devolucoes';
import DevolucaoDetalhe from './pages/devolucoes/DevolucaoDetalhe';
import { InstitutionProvider, useInstitution } from './contexts/InstitutionContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AjusteGradeProvider } from './pages/grade/AjusteGradeContext';
import { GradeTurmasProvider } from './pages/grade/GradeTurmasContext';
import Login from './pages/acesso/Login';
import Registrar from './pages/acesso/Registrar';
import EsqueciSenha from './pages/acesso/EsqueciSenha';
import VincularProfessor from './pages/seguranca/VincularProfessor';
import AprovarCadastro from './pages/acesso/AprovarCadastro';
import TrocarSenha from './pages/acesso/TrocarSenha';
import AdminUsuarios from './pages/seguranca/AdminUsuarios';
import AttendanceList from './Components/AttendanceList';
import JustificativasFaltaModal from './Components/JustificativasFaltaModal';
import DailyReport from './pages/painel/DailyReport';
import DiasSemAula from './pages/agenda/DiasSemAula';
import HistoricoAlunoMaster from './pages/matriculas/HistoricoAlunoMaster';
import Notificacoes from './pages/notificacoes/Notificacoes';
import EstatisticasComparativas from './pages/painel/EstatisticasComparativas';
import RecursosSistema from './pages/seguranca/RecursosSistema';
import AlunoLogin from './pages/portal-aluno/AlunoLogin';
import AlunoPerfil from './pages/portal-aluno/AlunoPerfil';
import { FeedbackProvider } from './Components/Feedback';
import { API_URL } from './api/config';

// Impede que um gesto de rolagem horizontal (dois dedos no trackpad, comum
// pra rolar as tabelas largas do sistema) seja interpretado pelo navegador
// como "voltar página" quando não há mais nada pra rolar naquela direção.
// `overscroll-behavior-x: none` (ver index.css/App.css) deveria bastar, mas
// em alguns notebooks o gesto chega ao Chrome/Edge de um jeito que não passa
// pela cadeia normal de scroll do CSS — por isso o reforço aqui, direto no
// wheel event, ANTES de decidir se deixa passar.
//
// Sobe pela árvore a partir do alvo do evento procurando o primeiro
// ancestral que realmente rola na horizontal (overflow-x ativo E conteúdo
// maior que o próprio tamanho) — não é só a tabela largas de cada tela
// (AjusteGrade, Turmas, Grade por Turma etc.), mas qualquer uma, sem precisar
// listar cada uma aqui.
const encontrarScrollHorizontal = (elemento) => {
  let el = elemento;
  while (el && el !== document.body && el !== document.documentElement) {
    const estilo = getComputedStyle(el);
    const podeRolar =
      (estilo.overflowX === 'auto' || estilo.overflowX === 'scroll') &&
      el.scrollWidth > el.clientWidth;
    if (podeRolar) return el;
    el = el.parentElement;
  }
  return null;
};

// Se o gesto for majoritariamente horizontal: sem nenhum ancestral que role
// de verdade na horizontal (ex.: rolando em cima do card de Filtros, que não
// tem overflow nenhum), cancela na hora — não tem pra onde ir mesmo, então
// nunca deve "escapar" pro navegador. Com ancestral rolável, só cancela
// quando ele já está na borda (início/fim) na direção do gesto — o "resto"
// do gesto que o navegador aproveitava pra navegar; no meio da rolagem,
// deixa passar normalmente.
const handleWheelGlobal = (e) => {
  if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
  const container = encontrarScrollHorizontal(e.target);
  if (!container) {
    e.preventDefault();
    return;
  }
  const maxScroll = container.scrollWidth - container.clientWidth;
  const noInicio = container.scrollLeft <= 0 && e.deltaX < 0;
  const noFim = container.scrollLeft >= maxScroll && e.deltaX > 0;
  if (noInicio || noFim) e.preventDefault();
};

// Data atual no formato YYYY-MM-DD, no fuso horário LOCAL do navegador.
// new Date().toISOString() sozinho usa UTC, o que erraria o dia perto da
// meia-noite; por isso soma o offset do fuso antes de converter para ISO.
const getTodayString = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const date = new Date(d.getTime() - offset * 60 * 1000);
  return date.toISOString().split('T')[0];
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Tela inicial (rota "/"): filtros (data/turno/transporte/professor) + a lista de
// chamada em si (delegada a <AttendanceList>, que faz a leitura/gravação de presença).
function Chamada() {
  const { institutionId } = useInstitution();
  const { token } = useAuth();

  const [dataAtual, setDataAtual] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const dateFromUrl = params.get('date');
    if (dateFromUrl && /^\d{4}-\d{2}-\d{2}$/.test(dateFromUrl)) {
      return dateFromUrl;
    }
    return getTodayString();
  });

  // Data de "hoje" de verdade, segundo o SERVIDOR — distinta de `dataAtual`
  // (a data ESCOLHIDA, que o usuário pode mudar pro passado/futuro no
  // seletor). Repassada pra <AttendanceList> pra decidir se `dataAtual` já
  // passou, sem depender do relógio do aparelho (ver getTodayLocal lá).
  const [hojeServidor, setHojeServidor] = useState(null);

  // Corrige a data padrão pela data do SERVIDOR assim que a tela abre — o valor
  // acima (getTodayString) usa o relógio do aparelho só como um chute inicial
  // pra não deixar a tela em branco; se o relógio do aparelho estiver errado
  // (fuso trocado, data desconfigurada), essa chamada substitui pelo dia real.
  // Não mexe em `dataAtual` se a URL já pediu uma data específica (link/filtro
  // deliberado) — a menos que ela seja futura: chamada de dia que ainda não
  // aconteceu não existe, então volta para hoje. `hojeServidor` é sempre atualizado.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    fetch(`${API_URL}/api/hoje`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        const data = body?.data ?? body;
        if (!data?.hoje) return;
        setHojeServidor(data.hoje);
        const dataDaUrl = params.get('date');
        if (!dataDaUrl || dataDaUrl > data.hoje) setDataAtual(data.hoje);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O `max` do seletor sozinho não basta: o do celular (iPhone) ignora o
  // limite, então uma data futura escolhida volta para hoje.
  const escolherData = (valor) => {
    const hoje = hojeServidor || getTodayString();
    setDataAtual(valor > hoje ? hoje : valor);
  };
  const [turnoSelecionado, setTurnoSelecionado] = useState('');
  const [transporteSelecionado, setTransporteSelecionado] = useState('');
  const [listaTransportes, setListaTransportes] = useState([]);
  const [professorSelecionado, setProfessorSelecionado] = useState('');
  const [listaProfessores, setListaProfessores] = useState([]);
  const [loadingTransportes, setLoadingTransportes] = useState(false);
  const [loadingProfessores, setLoadingProfessores] = useState(false);

  // Modal de "Justificativas de Falta" (ver JustificativasFaltaModal) — a
  // versão sobe a cada criação/edição/remoção salva lá, forçando a
  // <AttendanceList> a recarregar a lista que alimenta o dropdown de cada
  // aluno (os dois componentes buscam essa lista de forma independente).
  const [justificativasModalAberto, setJustificativasModalAberto] = useState(false);
  const [justificativasVersion, setJustificativasVersion] = useState(0);

  // Busca a lista de transportes
  useEffect(() => {
    if (!institutionId || !turnoSelecionado) {
      setListaTransportes([]);
      return;
    }

    const fetchTransportes = async () => {
      setLoadingTransportes(true);
      try {
        const timestamp = Date.now();
        const response = await fetch(
          `${API_URL}/api/transportes?data=${dataAtual}&t=${timestamp}`,
          {
            headers: { 'x-institution-id': institutionId, Authorization: `Bearer ${token}` },
          },
        );
        if (response.ok) {
          const body = await response.json();
          const data = body?.data ?? (Array.isArray(body) ? body : []);
          const formatado = Array.isArray(data)
            ? data.map((t) => {
                const v = typeof t === 'string' ? t : t.transporte || t.nome || t.nome_transporte;
                return typeof v === 'string' ? v.trim() : v;
              })
            : [];
          const finalData = [...new Set(formatado.filter(Boolean))].sort();
          setListaTransportes(finalData);
        } else {
          setListaTransportes([]);
        }
      } catch (error) {
        console.error('Erro ao carregar transportes:', error);
      } finally {
        setLoadingTransportes(false);
      }
    };

    fetchTransportes();
  }, [institutionId, turnoSelecionado, dataAtual, token]);

  // Busca a lista de professores
  useEffect(() => {
    if (!institutionId || !turnoSelecionado) {
      setListaProfessores([]);
      return;
    }

    const fetchProfessores = async () => {
      setLoadingProfessores(true);
      try {
        const timestamp = Date.now();
        const response = await fetch(`${API_URL}/api/professores?t=${timestamp}`, {
          headers: { 'x-institution-id': institutionId, Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const body = await response.json();
          const data = body?.data ?? (Array.isArray(body) ? body : []);
          const formatado = Array.isArray(data)
            ? data.map((p) =>
                typeof p === 'string' ? p.trim() : (p.nome || p.nome_professor || '').trim(),
              )
            : [];
          const finalData = [...new Set(formatado.filter(Boolean))].sort();
          setListaProfessores(finalData);
        } else {
          setListaProfessores([]);
        }
      } catch (error) {
        console.error('Erro ao carregar professores:', error);
        setListaProfessores([]);
      } finally {
        setLoadingProfessores(false);
      }
    };

    fetchProfessores();
  }, [institutionId, turnoSelecionado, dataAtual, token]);

  // Mantém a data selecionada na URL (?date=...) — permite recarregar a página ou
  // compartilhar o link mantendo a mesma data (ver leitura de `dateFromUrl` acima).
  useEffect(() => {
    const newUrl = `${window.location.pathname}?date=${dataAtual}`;
    window.history.pushState({}, '', newUrl);
  }, [dataAtual]);

  // Reseta o transporte selecionado se ele não existir no novo turno/data carregado
  useEffect(() => {
    if (transporteSelecionado && !listaTransportes.includes(transporteSelecionado)) {
      setTransporteSelecionado('');
    }
  }, [listaTransportes, transporteSelecionado]);

  // Reseta o professor selecionado se ele não existir no novo turno/data carregado
  useEffect(() => {
    if (professorSelecionado && !listaProfessores.includes(professorSelecionado)) {
      setProfessorSelecionado('');
    }
  }, [listaProfessores, professorSelecionado]);

  return (
    <div className="py-4">
      {/* Filters card */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-2.5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
            <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Filtros da Chamada
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <label
              htmlFor="date-picker"
              className="text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              Data
            </label>
            <div className="relative">
              <input
                type="date"
                id="date-picker"
                value={dataAtual}
                onChange={(e) => escolherData(e.target.value)}
                max={hojeServidor || getTodayString()}
                className="w-full h-10 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="turno-select"
              className="text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              Turno
            </label>
            <div className="relative">
              <select
                id="turno-select"
                value={turnoSelecionado}
                onChange={(e) => setTurnoSelecionado(e.target.value)}
                className="w-full h-10 px-3 pr-8 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none"
              >
                <option value="">Selecione o Turno...</option>
                <option value="Manhã">Manhã</option>
                <option value="Tarde">Tarde</option>
                <option value="Noite">Noite</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="transporte-select"
              className="text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              Transporte
            </label>
            <div className="relative">
              <select
                id="transporte-select"
                value={transporteSelecionado}
                onChange={(e) => setTransporteSelecionado(e.target.value)}
                disabled={!turnoSelecionado || loadingTransportes}
                className="w-full h-10 px-3 pr-8 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:text-gray-400"
              >
                <option value="">
                  {!turnoSelecionado
                    ? 'Selecione primeiro o turno...'
                    : loadingTransportes
                      ? 'Carregando...'
                      : 'Todos'}
                </option>
                {listaTransportes.map((transporte) => (
                  <option key={transporte} value={transporte}>
                    {transporte}
                  </option>
                ))}
              </select>
              <Bus className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="professor-select"
              className="text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              Educador
            </label>
            <div className="relative">
              <select
                id="professor-select"
                value={professorSelecionado}
                onChange={(e) => setProfessorSelecionado(e.target.value)}
                disabled={!turnoSelecionado || loadingProfessores}
                className="w-full h-10 px-3 pr-8 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:text-gray-400"
              >
                <option value="">
                  {!turnoSelecionado
                    ? 'Selecione primeiro o turno...'
                    : loadingProfessores
                      ? 'Carregando...'
                      : 'Selecione o Educador...'}
                </option>
                {listaProfessores.map((professor) => (
                  <option key={professor} value={professor}>
                    {professor}
                  </option>
                ))}
              </select>
              <ProfessorIcon className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Attendance card */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col">
        <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Lista de Chamada
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
              Marque as presenças do dia
            </span>
            <button
              type="button"
              onClick={() => setJustificativasModalAberto(true)}
              title="Gerenciar justificativas de falta"
              className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 flex-shrink-0"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-3">
          <AttendanceList
            dataAtual={dataAtual}
            hojeServidor={hojeServidor}
            turno={turnoSelecionado}
            transporte={transporteSelecionado}
            professor={professorSelecionado}
            justificativasVersion={justificativasVersion}
          />
        </div>
      </div>

      <JustificativasFaltaModal
        isOpen={justificativasModalAberto}
        onClose={() => setJustificativasModalAberto(false)}
        onChange={() => setJustificativasVersion((v) => v + 1)}
      />

    </div>
  );
}

// Duas camadas de proteção de rota, sempre nessa ordem (AuthRoute por fora,
// ProtectedRoute por dentro — ver as rotas em <App> abaixo):
//   1. AuthRoute: exige estar logado. Sem isso, nem entra no app (vai pra /login).
//   2. ProtectedRoute: exige ter uma instituição selecionada. Um usuário logado
//      mas sem instituição escolhida cai em /selecionar antes de acessar telas
//      que dependem de req.id_instituicao no backend.
const AuthRoute = ({ children }) => {
  const { isAuthenticated, loadingAuth, user } = useAuth();
  if (loadingAuth)
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#F9FAFB] dark:bg-gray-950">
        <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
          <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-700 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-sm">Carregando sessão...</p>
        </div>
      </div>
    );
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Sessão de aluno (token/user do mesmo AuthContext, mas com perfil 'aluno')
  // não tem lugar nenhuma tela da equipe — sem isso, um navegador com um
  // código de acesso salvo ficava preso tentando abrir telas normais (nenhum
  // guard abaixo sabia o que fazer com perfil 'aluno', e a rota "/" acabava
  // se redirecionando pra si mesma).
  if (user?.perfil === 'aluno') return <Navigate to="/aluno/perfil" replace />;
  return children;
};

const ProtectedRoute = ({ children }) => {
  const { institutionId } = useInstitution();
  if (!institutionId) {
    return <Navigate to="/selecionar" replace />;
  }
  return children;
};

// Bloqueia acesso direto por URL a telas fora do menu do perfil logado (ver
// menuItemsBase em Components/Layout.jsx, que usa a mesma lista de perfis por
// item — sem isso, esconder o item do menu não impediria digitar a rota).
// Sem `perfis` nem `tela`, a rota é liberada pra qualquer perfil autenticado.
//
// Duas formas de restringir, conforme a tela:
// - `perfis={[...]}` — FIXO no código, não editável pela tela de Permissões
//   (usado só pelas 2 telas com redirecionamento hardcoded pra master dentro
//   do próprio componente: AdminUsuarios.js, HistoricoAlunoMaster.js).
// - `tela="/caminho"` — DINÂMICO: checa as telas liberadas na INSTITUIÇÃO
//   ATIVA (ver GET /minhas-permissoes em backend/auth.js e
//   InstitutionContext.js — permissões são por instituição agora, não dá
//   mais pra cravar isso no token do login), editável pelo master na tela
//   "Permissões" (frontend/src/Permissoes.js). `master` sempre passa, mesmo
//   sem estar na lista (nunca fica trancado fora).
const PerfilRoute = ({ perfis, tela, children }) => {
  const { user } = useAuth();
  const { permissoes, carregandoPermissoes } = useInstitution();

  if (tela && user?.perfil !== 'master') {
    // Espera a busca de permissões da instituição ativa terminar antes de
    // decidir — sem isso, exibia tela em branco enquanto carregava
    if (carregandoPermissoes) {
      return (
        <div className="flex h-screen w-full items-center justify-center bg-[#F9FAFB] dark:bg-gray-950">
          <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
            <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-700 border-t-blue-500 rounded-full animate-spin"></div>
            <p className="text-sm">Carregando permissões...</p>
          </div>
        </div>
      );
    }
  }

  const telasLiberadas = permissoes?.telas_permitidas || [];
  // A primeira tela liberada para o perfil nesta instituição
  const primeiraTelaLiberada = telasLiberadas[0];

  // Helper para decidir o melhor destino seguro de redirecionamento quando o perfil não puder ver a rota atual
  const obterDestinoSeguro = () => {
    if (user?.perfil === 'professor' && telasLiberadas.includes('/grade')) {
      return '/grade';
    }
    if (primeiraTelaLiberada && primeiraTelaLiberada !== tela) {
      return primeiraTelaLiberada;
    }
    return null;
  };

  if (perfis && !perfis.includes(user?.perfil)) {
    const destino = obterDestinoSeguro() || (user?.perfil === 'professor' ? '/grade' : '/');
    return <Navigate to={destino} replace />;
  }

  if (tela && user?.perfil !== 'master') {
    if (!telasLiberadas.includes(tela)) {
      const destino = obterDestinoSeguro();
      if (destino) {
        return <Navigate to={destino} replace />;
      }
      // Se não há nenhuma tela liberada para este perfil nesta instituição,
      // avisa o usuário e oferece trocar de instituição para evitar loop infinito
      return (
        <div className="flex h-screen w-full items-center justify-center bg-[#F9FAFB] dark:bg-gray-950 p-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-900 p-6 rounded-2xl border border-gray-200 dark:border-gray-800 text-center shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Acesso Não Configurado
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Seu perfil não possui telas liberadas nesta instituição. Entre em contato com a coordenação ou selecione outra unidade.
            </p>
            <a
              href="/selecionar"
              className="inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              Trocar Instituição
            </a>
          </div>
        </div>
      );
    }
  }
  return children;
};

// Guarda própria pro aluno — sessão é a mesma (token/user do AuthContext),
// mas o redirecionamento de "não autenticado" vai pra /aluno/entrar (código de
// acesso), não pra /login (e-mail/senha da equipe). Sem Layout (sidebar,
// seletor de instituição) — a tela do aluno é uma página mobile isolada.
const AlunoRoute = ({ children }) => {
  const { isAuthenticated, loadingAuth, user } = useAuth();
  if (loadingAuth)
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#F9FAFB] dark:bg-gray-950">
        <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-700 border-t-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  if (!isAuthenticated || user?.perfil !== 'aluno') return <Navigate to="/aluno/entrar" replace />;
  return children;
};

function App() {
  useEffect(() => {
    document.addEventListener('wheel', handleWheelGlobal, { passive: false });
    return () => document.removeEventListener('wheel', handleWheelGlobal);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <FeedbackProvider>
        <AuthProvider>
          <InstitutionProvider>
            <AjusteGradeProvider>
              <GradeTurmasProvider>
                <Router>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/registrar" element={<Registrar />} />
                    <Route path="/esqueci-senha" element={<EsqueciSenha />} />
                    <Route path="/aprovar-cadastro/:token" element={<AprovarCadastro />} />
                    <Route path="/aluno/entrar" element={<AlunoLogin />} />
                    <Route
                      path="/aluno/perfil"
                      element={
                        <AlunoRoute>
                          <AlunoPerfil />
                        </AlunoRoute>
                      }
                    />
                    <Route
                      path="/selecionar"
                      element={
                        <AuthRoute>
                          <SelecionarInstituicao />
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/trocar-senha"
                      element={
                        <AuthRoute>
                          <Layout>
                            <TrocarSenha />
                          </Layout>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/admin-usuarios"
                      element={
                        <AuthRoute>
                          <PerfilRoute tela="/admin-usuarios">
                            <Layout>
                              <AdminUsuarios />
                            </Layout>
                          </PerfilRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/vincular-professor"
                      element={
                        <AuthRoute>
                          <PerfilRoute tela="/vincular-professor">
                            <Layout>
                              <VincularProfessor />
                            </Layout>
                          </PerfilRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/listas"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/listas">
                              <Layout>
                                <Listas />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/listas/:id"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/listas">
                              <Layout>
                                <ListaDetalhe />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/termos"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/termos">
                              <Layout>
                                <Termos />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/termos/:id"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/termos">
                              <Layout>
                                <TermoDetalhe />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/devolucoes"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/devolucoes">
                              <Layout>
                                <Devolucoes />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/devolucoes/:id"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/devolucoes">
                              <Layout>
                                <DevolucaoDetalhe />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/historico-aluno"
                      element={
                        <AuthRoute>
                          <PerfilRoute perfis={['master', 'coordenador']}>
                            <Layout>
                              <HistoricoAlunoMaster />
                            </Layout>
                          </PerfilRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/estatisticas-comparativas"
                      element={
                        <AuthRoute>
                          <PerfilRoute tela="/estatisticas-comparativas">
                            <Layout>
                              <EstatisticasComparativas />
                            </Layout>
                          </PerfilRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/recursos-sistema"
                      element={
                        <AuthRoute>
                          <PerfilRoute perfis={['master']}>
                            <Layout>
                              <RecursosSistema />
                            </Layout>
                          </PerfilRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/">
                              <Layout>
                                <Chamada />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/relatorio-diario"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/relatorio-diario">
                              <Layout>
                                <DailyReport />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/grade"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/grade">
                              <Layout>
                                <Grade />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/ajuste-grade"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/ajuste-grade">
                              <Layout>
                                <AjusteGrade />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/grade-turmas"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/grade-turmas">
                              <Layout>
                                <GradeTurmas />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/turmas"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/turmas">
                              <Layout>
                                <Turmas />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/professores"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/professores">
                              <Layout>
                                <Professores />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/gerenciar-matriculas"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/gerenciar-matriculas">
                              <Layout>
                                <GerenciarMatriculas />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/bolsistas"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/bolsistas">
                              <Layout>
                                <Bolsistas />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/assistencia-social"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/assistencia-social">
                              <Layout>
                                <AssistenciaSocial />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/estoque"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/estoque">
                              <Layout>
                                <Estoque />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/gerenciar-matriculas/novo-aluno"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/gerenciar-matriculas">
                              <Layout>
                                <CadastrarAluno />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/gerenciar-matriculas/aluno/:id/editar"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/gerenciar-matriculas">
                              <Layout>
                                <CadastrarAluno />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/dias-sem-aula"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/dias-sem-aula">
                              <Layout>
                                <DiasSemAula />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/meritocracia"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/meritocracia">
                              <Layout>
                                <Meritocracia />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/ocorrencias"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/ocorrencias">
                              <Layout>
                                <Ocorrencias />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/feedback-day"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/feedback-day">
                              <Layout>
                                <FeedbackDay />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/carater"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/carater">
                              <Layout>
                                <CaraterAdmin />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/notas"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/notas">
                              <Layout>
                                <Notas />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/planejamento"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/planejamento">
                              <Layout>
                                <Planejamento />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/pontos"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/pontos">
                              <Layout>
                                <Pontos />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/notificacoes"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/notificacoes">
                              <Layout>
                                <Notificacoes />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/permissoes"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/permissoes">
                              <Layout>
                                <Permissoes />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/areas"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute perfis={['master']}>
                              <Layout>
                                <AreasConfig />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/foguinhos"
                      element={
                        <AuthRoute>
                          <ProtectedRoute>
                            <PerfilRoute tela="/foguinhos">
                              <Layout>
                                <Foguinhos />
                              </Layout>
                            </PerfilRoute>
                          </ProtectedRoute>
                        </AuthRoute>
                      }
                    />
                  </Routes>
                </Router>
              </GradeTurmasProvider>
            </AjusteGradeProvider>
          </InstitutionProvider>
        </AuthProvider>
      </FeedbackProvider>
    </QueryClientProvider>
  );
}

export default App;
